export type BuyNowCheckoutDraft = {
  variant_id: number;
  qty: number;
  product_id?: number | null;
  product_name: string;
  product_slug: string;
  image_url?: string | null;
  image_alt?: string | null;
  unit_price: number;
  compare_at_price?: number | null;
  currency_code: string;
  variant_name?: string | null;
  customization_enabled?: boolean;
  customization_color?: string | null;
  customization_note?: string | null;
};

const BUY_NOW_CHECKOUT_KEY = "store_buy_now_checkout_draft";

const toFiniteNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toCleanString = (value: unknown): string => String(value ?? "").trim();

const normalizeDraft = (value: unknown): BuyNowCheckoutDraft | null => {
  if (!value || typeof value !== "object") return null;

  const raw = value as Record<string, unknown>;
  const variantId = Math.trunc(toFiniteNumber(raw.variant_id) ?? 0);
  const qty = Math.max(1, Math.trunc(toFiniteNumber(raw.qty) ?? 0));
  const productName = toCleanString(raw.product_name);
  const productSlug = toCleanString(raw.product_slug);
  const currencyCode = toCleanString(raw.currency_code).toUpperCase() || "INR";
  const unitPrice = toFiniteNumber(raw.unit_price);

  if (variantId <= 0 || !productName || !productSlug || unitPrice == null || unitPrice < 0) {
    return null;
  }

  const compareAt = toFiniteNumber(raw.compare_at_price);
  const normalizedCompareAt =
    compareAt != null && compareAt > unitPrice ? compareAt : null;

  const productId = toFiniteNumber(raw.product_id);
  const imageUrl = toCleanString(raw.image_url) || null;
  const imageAlt = toCleanString(raw.image_alt) || null;
  const variantName = toCleanString(raw.variant_name) || null;
  const customizationColor = toCleanString(raw.customization_color) || null;
  const customizationNote = toCleanString(raw.customization_note) || null;
  const customizationEnabled =
    Boolean(raw.customization_enabled) || !!customizationColor || !!customizationNote;

  return {
    variant_id: variantId,
    qty,
    product_id: productId != null ? Math.trunc(productId) : null,
    product_name: productName,
    product_slug: productSlug,
    image_url: imageUrl,
    image_alt: imageAlt,
    unit_price: unitPrice,
    compare_at_price: normalizedCompareAt,
    currency_code: currencyCode,
    variant_name: variantName,
    customization_enabled: customizationEnabled,
    customization_color: customizationEnabled ? customizationColor : null,
    customization_note: customizationEnabled ? customizationNote : null,
  };
};

export const saveBuyNowCheckoutDraft = (draft: BuyNowCheckoutDraft): void => {
  if (typeof window === "undefined") return;

  const normalized = normalizeDraft(draft);
  if (!normalized) return;

  window.sessionStorage.setItem(BUY_NOW_CHECKOUT_KEY, JSON.stringify(normalized));
};

export const loadBuyNowCheckoutDraft = (): BuyNowCheckoutDraft | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(BUY_NOW_CHECKOUT_KEY);
    if (!raw) return null;
    return normalizeDraft(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const clearBuyNowCheckoutDraft = (): void => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(BUY_NOW_CHECKOUT_KEY);
};
